import {
  createRouter,
  createWebHistory,
  type NavigationGuardWithThis,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';
import { isLoggedIn } from 'axios-jwt';
import useAuthStore from '@/store/AuthStore';
import PageNotFoundView from '@/views/PageNotFoundView.vue';
import ProfileView from '@/views/ProfileView.vue';
import LoginView from '@/views/LoginView.vue';
import VisualizerView from '@/views/VisualizerView.vue';
import ResourcesView from '@/views/ResourcesView.vue';
import FleetView from '@/views/FleetView.vue';
import InternalFleetView from '@/views/InternalFleetView.vue';
import RobotView from '@/views/RobotView.vue';
import TutorialsView from '@/views/TutorialsView.vue';
import CloudRobflowView from '../views/CloudRobflowView.vue';
import ControlUnitsAdminView from '@/views/ControlUnitsAdminView.vue';
import EdgeDevicesAdminView from '@/views/EdgeDevicesAdminView.vue';
import UsersAdminView from '@/views/UsersAdminView.vue';
import CustomersAdminView from '@/views/CustomersAdminView.vue';
import HelpView from '@/views/HelpView.vue';
import DevaugeTangermundePalletizingView from '@/views/DevaugeTangermundePalletizingView.vue';
import DevaugeLuneburgPalletizingView from '@/views/DevaugeLuneburgPalletizingView.vue';
import CatalogueView from '@/views/CatalogueView.vue';
import RobVisionView from '@/views/RobVisionView.vue';
import VisionProjectView from '@/views/VisionProjectView.vue';
import OeeMetricsView from '@/views/OeeMetricsView.vue';
import NpsAdminView from '@/views/NpsAdminView.vue';
import HowItsMadeView from '@/views/HowItsMadeView.vue';
import { LOGIN_URL, LOGOUT_URL } from '@/Constants';
import { Permission } from '@/models/Roles';
import RobotFinderView from '@/views/RobotFinderView.vue';
import gettext from '@/i18n/gettext';
import useBreadcrumbStore, {
  type BreadcrumbItem,
} from '@/store/BreadcrumbStore';

const { $gettext } = gettext;

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Static breadcrumb segments for the route. Defined as a function so
     * $gettext is evaluated on render (reactive to language changes).
     * Dynamic segments (e.g. a robot serial number) are appended by the views
     * via useBreadcrumbStore().
     */
    breadcrumb?: () => BreadcrumbItem[];
  }
}

/**
 * Checks if the user has a specific role.
 * If the user does not have the required role, redirect to the 404 page.
 * If the permissions are not fetched, redirect to the LOGIN URL from cognito.
 * @param requiredPermission The permission required to access the route.
 */
const checkUserHasPermission =
  (requiredPermission: Permission): NavigationGuardWithThis<undefined> =>
  async (to, from, next) => {
    const authStore = useAuthStore();
    // Fast-fail if terms are not yet accepted to avoid waiting for permissions
    if (authStore.isAuthenticated && !authStore.termsAccepted) {
      authStore.setRedirectRoute(to.fullPath);
      return next({ path: '/login' });
    }
    try {
      await authStore.waitForPermissions();
    } catch {
      window.location.replace(LOGIN_URL);
    }
    if (authStore.hasPermission(requiredPermission)) {
      next();
    } else {
      next({ path: '/not-found' });
    }
  };

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'finder',
    meta: { breadcrumb: () => [{ title: $gettext('Robot Finder') }] },
    component: RobotFinderView,
    beforeEnter: checkUserHasPermission(Permission.GET_CALCULATOR),
  },
  {
    path: '/fleet-management',
    name: 'fleet-management',
    meta: { breadcrumb: () => [{ title: $gettext('Fleet Management') }] },
    component: FleetView,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
  },
  {
    path: '/tangermunde-palletizing',
    name: 'tangermunde-devauge-palletizing',
    meta: { breadcrumb: () => [{ title: $gettext('Palletizing status') }] },
    component: DevaugeTangermundePalletizingView,
    beforeEnter: checkUserHasPermission(
      Permission.GET_DEVAUGE_TANGERMUNDE_VARIABLES,
    ),
  },
  {
    path: '/luneburg-palletizing',
    name: 'luneburg-devauge-palletizing',
    meta: { breadcrumb: () => [{ title: $gettext('Palletizing status') }] },
    component: DevaugeLuneburgPalletizingView,
    beforeEnter: checkUserHasPermission(
      Permission.GET_DEVAUGE_LUNEBURG_VARIABLES,
    ),
  },
  {
    path: '/catalogue',
    name: 'catalogue',
    meta: { breadcrumb: () => [{ title: $gettext('Catalogue') }] },
    component: CatalogueView,
    beforeEnter: checkUserHasPermission(Permission.GET_CATALOGUE),
  },
  {
    path: '/visualizer',
    name: 'visualizer',
    meta: { breadcrumb: () => [{ title: $gettext('Robot Visualizer') }] },
    component: VisualizerView,
  },
  {
    path: '/fleet-management/detail',
    name: 'robot-details',
    meta: {
      breadcrumb: () => [
        { title: $gettext('Fleet Management'), to: '/fleet-management' },
      ],
    },
    component: RobotView,
  },
  {
    path: '/resources',
    name: 'resources',
    meta: { breadcrumb: () => [{ title: $gettext('Resources') }] },
    component: ResourcesView,
  },
  {
    path: '/tutorials',
    name: 'tutorials',
    meta: { breadcrumb: () => [{ title: $gettext('Tutorials') }] },
    component: TutorialsView,
  },
  {
    path: '/help',
    name: 'help',
    meta: { breadcrumb: () => [{ title: $gettext('Help') }] },
    component: HelpView,
  },
  {
    path: '/profile',
    name: 'profile',
    meta: { breadcrumb: () => [{ title: $gettext('Profile') }] },
    component: ProfileView,
  },
  {
    path: '/cloud-robflow',
    name: 'cloud-robflow',
    meta: { breadcrumb: () => [{ title: $gettext('Cloud RobFlow') }] },
    component: CloudRobflowView,
    beforeEnter: checkUserHasPermission(Permission.GET_CLOUD_ROBFLOW),
  },
  {
    path: '/metrics',
    name: 'metrics',
    meta: { breadcrumb: () => [{ title: $gettext('Metrics') }] },
    component: OeeMetricsView,
    beforeEnter: checkUserHasPermission(Permission.GET_OEE_METRICS),
  },
  {
    path: '/robvision',
    name: 'robvision',
    meta: { breadcrumb: () => [{ title: $gettext('RobVision') }] },
    component: RobVisionView,
    beforeEnter: checkUserHasPermission(Permission.GET_ROBVISION),
  },
  {
    path: '/robvision/projects/:projectUuid',
    name: 'robvision-project',
    meta: {
      breadcrumb: () => [{ title: $gettext('RobVision'), to: '/robvision' }],
    },
    component: VisionProjectView,
    beforeEnter: checkUserHasPermission(Permission.GET_ROBVISION),
  },
  {
    path: '/internal/fleet',
    name: 'internal-fleet',
    meta: {
      breadcrumb: () => [{ title: $gettext('Custom Fleet Management') }],
    },
    component: InternalFleetView,
    beforeEnter: checkUserHasPermission(Permission.INTERNAL_ACCESS),
  },
  {
    path: '/how-its-made',
    name: 'how-its-made',
    meta: { breadcrumb: () => [{ title: $gettext("How it's made") }] },
    component: HowItsMadeView,
    beforeEnter: checkUserHasPermission(Permission.GET_NPS),
  },
  {
    path: '/admin/nps',
    name: 'nps',
    meta: { breadcrumb: () => [{ title: $gettext('NPS') }] },
    component: NpsAdminView,
    beforeEnter: checkUserHasPermission(Permission.GET_NPS),
  },
  {
    path: '/admin/control-units',
    name: 'control-units',
    meta: { breadcrumb: () => [{ title: $gettext('Robots') }] },
    component: ControlUnitsAdminView,
    beforeEnter: checkUserHasPermission(Permission.GET_CONTROL_UNIT),
  },
  {
    path: '/admin/users',
    name: 'users',
    meta: { breadcrumb: () => [{ title: $gettext('Users') }] },
    component: UsersAdminView,
    beforeEnter: checkUserHasPermission(Permission.GET_USER),
  },
  {
    path: '/admin/edge-devices',
    name: 'edge-devices',
    meta: { breadcrumb: () => [{ title: $gettext('Edge Devices') }] },
    component: EdgeDevicesAdminView,
    beforeEnter: checkUserHasPermission(Permission.GET_EDGE_DEVICE),
  },
  {
    path: '/admin/customers',
    name: 'customers',
    meta: { breadcrumb: () => [{ title: $gettext('Customers') }] },
    component: CustomersAdminView,
    beforeEnter: checkUserHasPermission(Permission.GET_CUSTOMER),
  },
  {
    path: '/not-found',
    component: PageNotFoundView,
  },
  {
    // Fallback route to 404 page
    path: '/:pathMatch(.*)*',
    redirect: '/not-found',
  },
];

const router: Router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }
    return false;
  },
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  if (authStore.bootPromise) {
    try {
      await authStore.bootPromise;
    } catch {
      window.location.replace(LOGOUT_URL);
    }
  }

  if (to.path !== '/login' && !(await isLoggedIn())) {
    useAuthStore().setRedirectRoute(to.fullPath);
    window.location.replace(LOGIN_URL);
  }

  // If logged in but terms not accepted, always route to login
  if (
    to.path !== '/login' &&
    authStore.isAuthenticated &&
    !authStore.termsAccepted
  ) {
    authStore.setRedirectRoute(to.fullPath);
    return next({ path: '/login' });
  }

  next();
});

// Set the redirectRoute property if not on login page
router.afterEach((to, from) => {
  if (to.path !== '/login') {
    useAuthStore().setRedirectRoute(to.fullPath);
  }
  // Dynamic segments belong to the view that set them; drop them when the
  // view changes (query-only navigation, e.g. switching tabs, keeps them)
  if (to.path !== from.path) {
    useBreadcrumbStore().clearDynamicState();
  }
});

export default router;
